import {
  useEffect, useId, useRef, useState,
  type DragEvent, type KeyboardEvent, type ReactNode,
} from 'react';
import { GripVertical } from '../../../icons';
import { Switch } from '../Switch';
import './ItemToggleList.css';

/**
 * ItemToggleList — **보이는 항목 목록: 켜기 · 끌어서 순서** (2026-09-15 · 대시보드 시안 v2 V0 · 정본 제안 B7-3 `#ITEMROW` · 18).
 *
 * 쓰는 곳: 위젯 설정 「보이는 항목 · ⠿ 로 순서를 바꿉니다」(근태 관리 · 이번 주 요약 · 한눈에 · …).
 *
 * ★★★ **끌기와 키보드가 같은 길이다** (사용자 결정 Q-1 — 끌기 + 키보드). 끌기만 두면 키보드로는 순서를 못 바꾼다.
 *     · 손잡이에 포커스 → ↑ ↓ 로 한 칸씩
 *     · 줄 안 어디서든(스위치) → Alt+↑ / Alt+↓ (`MenuAccessPanel` 과 같은 약속)
 *     · 끌기는 **손잡이에서 시작할 때만** — 스위치를 누르려다 줄이 끌려가지 않게(`WidgetGrid` 와 같은 규칙).
 * ★★ **스크린리더에게 옮긴 결과를 말한다** — 「「결재 대기」 2번째로 옮김 · 전체 8개」(`role="status"`).
 *    옮긴 뒤에도 **포커스는 옮긴 줄의 손잡이에 남는다** — 한 번 더 누르면 계속 옮겨진다.
 * ★★ **막힌 항목은 켜기만 막는다** — 까닭을 줄 안에 적고 스위치에 읽어 준다. 순서는 바꿀 수 있다
 *    (막힘이 풀리면 그 자리에 선다 · 저장값을 지우지 않는다는 V1 규칙과 같다).
 * ★ 움직임은 줄 그림자 · 살짝 밀기뿐이다 — `prefers-reduced-motion` 이면 끈다.
 * ★ 이 부품은 **목록 순서를 들고 있지 않는다** — `onMove(from, to)` 로 알리고 부르는 쪽이 바꾼 목록을 다시 준다.
 */

export interface ItemToggleRow {
  key: string;
  /** 줄 이름 — 화면에 그린다. */
  label: ReactNode;
  /** 읽어 줄 이름(문자) — 옮김 알림 · 스위치 이름에 쓴다. */
  labelText: string;
  description?: ReactNode;
  on: boolean;
  /** 켜기를 막는 까닭 — 있으면 스위치가 잠긴다. */
  lockedReason?: string | null;
  /** 까닭 없이 켜기만 막는다(최대 개수에 닿음 등) — 끄는 것은 된다. */
  cannotTurnOn?: boolean;
}

export interface ItemToggleListProps {
  items: readonly ItemToggleRow[];
  onToggle: (key: string, next: boolean) => void;
  onMove: (from: number, to: number) => void;
  /** 목록 이름 — 「보이는 항목」. */
  ariaLabel: string;
  /** 순서 바꾸기를 끈다(메일의 「보이는 항목」처럼 순서가 뜻이 없는 목록). */
  reorderable?: boolean;
  disabled?: boolean;
  className?: string;
}

export function ItemToggleList({
  items, onToggle, onMove, ariaLabel, reorderable = true, disabled = false, className,
}: ItemToggleListProps) {
  const uid = useId();
  const helpId = `${uid}-help`;
  const [announce, setAnnounce] = useState('');
  const [dragKey, setDragKey] = useState<string | null>(null);
  const [overKey, setOverKey] = useState<string | null>(null);
  /** 옮긴 뒤 포커스를 돌려줄 줄. 부르는 쪽이 목록을 바꿔 다시 그린 다음에 돌려준다. */
  const [refocusKey, setRefocusKey] = useState<string | null>(null);
  const gripRefs = useRef(new Map<string, HTMLButtonElement>());
  const fromGrip = useRef(false);

  useEffect(() => {
    if (!refocusKey) return;
    gripRefs.current.get(refocusKey)?.focus();
    setRefocusKey(null);
  }, [items, refocusKey]);

  const moveBy = (index: number, delta: -1 | 1) => {
    const to = index + delta;
    if (disabled || to < 0 || to >= items.length) return false;
    const row = items[index]!;
    onMove(index, to);
    setAnnounce(`「${row.labelText}」 ${to + 1}번째로 옮김 · 전체 ${items.length}개`);
    setRefocusKey(row.key);
    return true;
  };

  const onGripKeyDown = (e: KeyboardEvent<HTMLButtonElement>, index: number) => {
    if (e.key !== 'ArrowUp' && e.key !== 'ArrowDown') return;
    e.preventDefault();
    moveBy(index, e.key === 'ArrowUp' ? -1 : 1);
  };

  const onRowKeyDown = (e: KeyboardEvent<HTMLLIElement>, index: number) => {
    if (!reorderable || !e.altKey || (e.key !== 'ArrowUp' && e.key !== 'ArrowDown')) return;
    e.preventDefault();
    moveBy(index, e.key === 'ArrowUp' ? -1 : 1);
  };

  const onDragStart = (e: DragEvent<HTMLLIElement>, key: string) => {
    // 손잡이에서 시작한 끌기만 받는다.
    if (!fromGrip.current || disabled) { e.preventDefault(); return; }
    e.dataTransfer?.setData?.('text/plain', key);
    if (e.dataTransfer) e.dataTransfer.effectAllowed = 'move';
    setDragKey(key);
  };

  const onDrop = (e: DragEvent<HTMLLIElement>, toIndex: number) => {
    e.preventDefault();
    const from = items.findIndex((r) => r.key === dragKey);
    setOverKey(null);
    setDragKey(null);
    fromGrip.current = false;
    if (from < 0 || from === toIndex) return;
    const row = items[from]!;
    onMove(from, toIndex);
    setAnnounce(`「${row.labelText}」 ${toIndex + 1}번째로 옮김 · 전체 ${items.length}개`);
  };

  return (
    <div className={['wc-itl', className ?? ''].filter(Boolean).join(' ')}>
      <ul className="wc-itl__list" aria-label={ariaLabel} aria-describedby={reorderable ? helpId : undefined}>
        {items.map((row, index) => {
          const reasonId = row.lockedReason ? `${uid}-why-${row.key}` : undefined;
          const switchDisabled = disabled || Boolean(row.lockedReason) || (!row.on && row.cannotTurnOn === true);
          return (
            <li
              key={row.key}
              className={[
                'wc-itl__row',
                row.lockedReason ? 'is-locked' : '',
                dragKey === row.key ? 'is-dragging' : '',
                overKey === row.key && dragKey !== row.key ? 'is-over' : '',
              ].filter(Boolean).join(' ')}
              draggable={reorderable && !disabled}
              onDragStart={(e) => onDragStart(e, row.key)}
              onDragOver={(e) => { if (dragKey) { e.preventDefault(); setOverKey(row.key); } }}
              onDragLeave={() => setOverKey((k) => (k === row.key ? null : k))}
              onDrop={(e) => onDrop(e, index)}
              onDragEnd={() => { setDragKey(null); setOverKey(null); fromGrip.current = false; }}
              onKeyDown={(e) => onRowKeyDown(e, index)}
            >
              {reorderable && (
                <button
                  type="button"
                  className="wc-itl__grip"
                  aria-label={`${row.labelText} 순서 옮기기`}
                  aria-describedby={helpId}
                  disabled={disabled}
                  ref={(el) => { if (el) gripRefs.current.set(row.key, el); else gripRefs.current.delete(row.key); }}
                  onMouseDown={() => { fromGrip.current = true; }}
                  onMouseUp={() => { fromGrip.current = false; }}
                  onKeyDown={(e) => onGripKeyDown(e, index)}
                >
                  <GripVertical size={14} aria-hidden />
                </button>
              )}
              <span className="wc-itl__text">
                <span className="wc-itl__label">{row.label}</span>
                {row.description != null && !row.lockedReason && <span className="wc-itl__desc">{row.description}</span>}
                {row.lockedReason && <span id={reasonId} className="wc-itl__why">{row.lockedReason}</span>}
              </span>
              <Switch
                className="wc-itl__switch"
                checked={row.on && !row.lockedReason}
                onCheckedChange={(next) => onToggle(row.key, next)}
                disabled={switchDisabled}
                aria-label={row.labelText}
                aria-describedby={reasonId}
              />
            </li>
          );
        })}
      </ul>
      {reorderable && (
        <p id={helpId} className="wc-itl__sr">
          손잡이에서 위 · 아래 화살표로 순서를 바꿉니다. 줄 안에서는 Alt 와 위 · 아래 화살표로도 옮깁니다. 손잡이를 끌어 옮길 수도 있습니다.
        </p>
      )}
      <div className="wc-itl__sr" role="status" aria-live="polite">{announce}</div>
    </div>
  );
}
